import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Cookies from "js-cookie";
import api from "../../chat/api";

/* The "/" menu.
 *
 * Two modes in one popover. Typing "/" lists commands; picking one that takes an
 * argument (today only /project) swaps the body for that command's picker while
 * the composer text becomes the search term. Keeping it in one surface means the
 * list never jumps position under the cursor mid-selection.
 *
 * Projects load a page at a time. A workspace with 300 projects should not send
 * 300 rows to render a menu, and "Show more" is honest about there being more —
 * an infinite scroller inside a popover this small mostly just hides things.
 */

const PAGE = 10;

/* Projects are attached with @, not a slash command. The split follows the
 * Opened from the "+" button beside the input rather than by a typed trigger.
 * A character you have to know about is a feature only the people who read the
 * placeholder ever find; a button is visible to everyone, and attaching a
 * project is common enough to deserve a permanent target. Not listed in
 * COMMANDS — nothing typed reaches it. */
export const PROJECT_COMMAND = {
  key: "project",
  trigger: "@",
  label: "Project",
  hint: "Attach a project to this conversation",
  takesArgument: true,
  icon: (
    <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2.5h8a2 2 0 0 1 2 2V17a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </svg>
  ),
};

export const COMMANDS = [
  {
    key: "summary",
    trigger: "/summary",
    label: "Summary",
    hint: "Summarize this project for me",
    takesArgument: false,
    icon: (
      <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 6h16" />
        <path d="M4 12h16" />
        <path d="M4 18h10" />
      </svg>
    ),
  },
  {
    key: "qualify",
    trigger: "/qualify",
    label: "Qualify",
    hint: "Check if we should bid on this project",
    takesArgument: false,
    icon: (
      <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3v3" />
        <path d="M5.2 7.2l2.1 2.1" />
        <path d="M18.8 7.2l-2.1 2.1" />
        <path d="M4 15a8 8 0 0 1 16 0" />
        <path d="M12 15l3.5-3.5" />
        <circle cx="12" cy="15" r="1.4" />
      </svg>
    ),
  },
];

// "@nv" at the tail -> the project picker filtered to "nv".
// "/qual" at the start -> the command list. Projects are no longer reachable
// from typed text at all — the "+" button owns that — so "@" is just a
// character again and an email address in a question stays an email address.
export function parseSlash(text) {
  const raw = String(text || "");
  if (!raw.startsWith("/")) return null;
  const m = raw.match(/^\/(\S*)(?:\s+([\s\S]*))?$/);
  if (!m) return null;
  const word = (m[1] || "").toLowerCase();
  const rest = m[2] ?? null;

  const exact = COMMANDS.find((c) => c.key === word);
  // The space is what commits the command. Without it "/quali" would fire
  // while the user is still typing the word.
  if (exact && rest !== null) return { mode: "argument", command: exact, query: rest.trim() };
  return { mode: "commands", query: word, matches: COMMANDS.filter((c) => c.key.startsWith(word)) };
}

const Row = ({ active, onPick, onHover, children, ...rest }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}   // keep the caret in the composer
    onClick={onPick}
    onMouseEnter={onHover}
    className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors duration-150 ${
      active ? "bg-[#A04A97]/[0.09]" : "hover:bg-[#18101C]/[0.04]"
    }`}
    {...rest}
  >
    {children}
  </button>
);

const CopilotSlashMenu = ({ parsed, onPickCommand, onPickProject, onClose, onQueryChange, registerKeyHandler }) => {
  const [projects, setProjects] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [nextOffset, setNextOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [active, setActive] = useState(0);

  const listRef = useRef(null);
  const reqRef = useRef(0);
  // The same key handler the composer forwards into, reachable by the
  // picker's own search input — one set of arrow/Enter semantics, two inputs.
  const keyFnRef = useRef(null);

  const mode = parsed?.mode;
  const query = parsed?.query || "";
  const wantsProjects = mode === "argument" && parsed?.command?.key === "project";

  const fetchPage = useCallback(async (q, offset) => {
    // Every request carries a sequence number; a slower earlier response can
    // otherwise land after a newer one and repopulate the list with stale rows.
    const seq = ++reqRef.current;
    const res = await api.get("/api/v1/rfp/copilot/projects/", {
      params: {
        q: q || undefined,
        offset,
        limit: PAGE,
        organization_id: Cookies.get("selected_org_id") || undefined,
      },
    });
    return { seq, data: res?.data || {} };
  }, []);

  // First page, refetched as the search term changes.
  useEffect(() => {
    if (!wantsProjects) return undefined;
    let cancelled = false;
    setLoading(true);
    setActive(0);
    const t = setTimeout(async () => {
      try {
        const { seq, data } = await fetchPage(query, 0);
        if (cancelled || seq !== reqRef.current) return;
        setProjects(data.projects || []);
        setTotal(data.total || 0);
        setHasMore(!!data.has_more);
        setNextOffset(data.next_offset || 0);
        setError(null);
      } catch {
        if (!cancelled) setError("Couldn't load projects.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, query ? 180 : 0);   // debounce typing, but open instantly
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [wantsProjects, query, fetchPage]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const { seq, data } = await fetchPage(query, nextOffset);
      if (seq !== reqRef.current) return;
      setProjects((prev) => {
        // Guard against a duplicate page if "Show more" is double-clicked.
        const seen = new Set(prev.map((p) => p.id));
        return [...prev, ...(data.projects || []).filter((p) => !seen.has(p.id))];
      });
      setHasMore(!!data.has_more);
      setNextOffset(data.next_offset || nextOffset);
    } catch {
      setError("Couldn't load more.");
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, fetchPage, query, nextOffset]);

  const rows = useMemo(() => {
    if (mode === "commands") return parsed?.matches || [];
    if (mode === "argument") return projects;
    return [];
  }, [mode, parsed, projects]);

  // Exactly the condition the render uses. The two MUST agree: the key handler
  // is only legitimate while the menu is on screen, and this is the single place
  // that decides whether it is.
  const isOpen = !!parsed && !(mode === "commands" && !(parsed.matches || []).length);

  const commit = useCallback(
    (index) => {
      if (!isOpen) return;
      if (mode === "commands") {
        const cmd = (parsed?.matches || [])[index];
        if (cmd) onPickCommand?.(cmd);
        return;
      }
      // One past the end is the "Show more" row.
      if (index === projects.length && hasMore) {
        loadMore();
        return;
      }
      const p = projects[index];
      if (p) onPickProject?.(p);
    },
    [isOpen, mode, parsed, projects, hasMore, loadMore, onPickCommand, onPickProject]
  );

  // Arrow keys and Enter belong to the menu ONLY while it is on screen.
  //
  // Hooks run before this component's `return null`, so without the isOpen guard
  // the handler stays registered after the menu closes — and because `projects`
  // survives in state, Enter gets swallowed and routed into commit(), re-picking
  // a project and wiping the draft instead of sending the message.
  useEffect(() => {
    if (!isOpen) {
      registerKeyHandler?.(null);
      return undefined;
    }
    const max = rows.length + (mode === "argument" && hasMore ? 1 : 0);
    const handler = (e) => {
      if (!max) {
        if (e.key === "Escape") { onClose?.(); return true; }
        return false;
      }
      if (e.key === "ArrowDown") { setActive((i) => (i + 1) % max); return true; }
      if (e.key === "ArrowUp") { setActive((i) => (i - 1 + max) % max); return true; }
      if (e.key === "Enter" || e.key === "Tab") { commit(active); return true; }
      if (e.key === "Escape") { onClose?.(); return true; }
      return false;
    };
    keyFnRef.current = handler;
    registerKeyHandler?.(handler);
    return () => {
      keyFnRef.current = null;
      registerKeyHandler?.(null);
    };
  }, [isOpen, rows.length, hasMore, mode, active, commit, onClose, registerKeyHandler]);

  // Drop the fetched page once the picker closes, so a reopened picker cannot
  // flash the previous search's results before the new ones land.
  useEffect(() => {
    if (mode === "argument") return;
    setProjects([]);
    setTotal(0);
    setHasMore(false);
    setNextOffset(0);
  }, [mode]);

  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${active}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [active]);

  if (!isOpen) return null;

  return (
    <div className="mx-auto mb-2 w-full max-w-[860px]">
      <div className="overflow-hidden rounded-2xl border border-[#18101C]/[0.08] bg-white shadow-[0_1px_2px_rgba(24,16,28,0.04),0_16px_40px_-20px_rgba(24,16,28,0.45)] animate-copilot-rise motion-reduce:animate-none">
        <div className="flex items-center justify-between border-b border-[#18101C]/[0.06] px-3 py-2">
          <span className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[#77707E]">
            {mode === "commands" ? "Commands" : parsed.command.label}
          </span>
          {mode === "argument" && !loading && (
            <span className="text-[12px] text-[#77707E]">
              {query ? `${total} match${total === 1 ? "" : "es"}` : `${total} project${total === 1 ? "" : "s"}`}
            </span>
          )}
        </div>

        {mode === "argument" && (
          /* A real input, not a decoration: it autofocuses when the picker
             opens so the next keystroke searches, and it edits the same draft
             string the composer holds, so the two can never disagree. Arrows,
             Enter and Escape forward into the one shared key handler. */
          <div className="relative border-b border-[#18101C]/[0.06] px-3 py-2">
            <svg aria-hidden="true"
              className="pointer-events-none absolute left-[1.45rem] top-1/2 -translate-y-1/2 text-[#77707E]"
              width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
            >
              <circle cx="11" cy="11" r="7" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              autoFocus
              value={query}
              aria-label={`Search ${parsed.command.key === "project" ? "projects" : "options"}`}
              placeholder={`Search ${parsed.command.key === "project" ? "projects" : "options"}…`}
              onChange={(e) =>
                /* The picker owns its own search box now. It used to write the
                   query back into the DRAFT as "<trigger> text", because the
                   draft was what opened it; opened from a button there is no
                   trigger in the draft to keep in sync, and echoing the search
                   into the message the user is composing would be nonsense. */
                parsed.picker
                  ? onQueryChange?.(e.target.value)
                  : onQueryChange?.(`${parsed.command.trigger} ${e.target.value}`)
              }
              onKeyDown={(e) => {
                // Backspace on an empty box steps back out to the command list,
                // matching how deleting past the space behaves in the composer.
                if (e.key === "Backspace" && !query) {
                  e.preventDefault();
                  onQueryChange?.(parsed.command.trigger);
                  return;
                }
                if (keyFnRef.current?.(e)) e.preventDefault();
              }}
              className="w-full rounded-lg border border-transparent bg-[#18101C]/[0.04] py-[6px] pl-7 pr-3 text-[14px] text-[#18101C] outline-none transition-colors duration-200 placeholder:text-[#77707E] focus:border-[#A04A97]/40 focus:bg-white"
            />
          </div>
        )}

        <div ref={listRef} className="sensa-scroll max-h-[280px] overflow-y-auto p-1.5">
          {mode === "commands" &&
            parsed.matches.map((c, i) => (
              <Row key={c.key} data-idx={i} active={i === active} onHover={() => setActive(i)} onPick={() => commit(i)}>
                <span className="flex h-7 w-7 flex-none items-center justify-center rounded-md bg-[#A04A97]/[0.09] text-[#A04A97]">
                  {c.icon}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[14px] font-medium text-[#18101C]">{c.trigger}</span>
                  <span className="block truncate text-[12.5px] text-[#77707E]">{c.hint}</span>
                </span>
              </Row>
            ))}

          {mode === "argument" && loading &&
            [0, 1, 2, 3].map((i) => (
              <div key={i} className="px-2.5 py-2">
                <div className="h-[11px] w-[55%] animate-pulse rounded bg-[#18101C]/[0.07]" />
                <div className="mt-1.5 h-[9px] w-[30%] animate-pulse rounded bg-[#18101C]/[0.045]" />
              </div>
            ))}

          {mode === "argument" && !loading && error && (
            <p className="px-2.5 py-3 text-[13px] text-[#B91C1C]">{error}</p>
          )}

          {mode === "argument" && !loading && !error && !projects.length && (
            <p className="px-2.5 py-4 text-center text-[13.5px] text-[#77707E]">
              {query ? `No project matches “${query}”.` : "You have no projects yet."}
            </p>
          )}

          {mode === "argument" && !loading &&
            projects.map((p, i) => (
              <Row key={p.id} data-idx={i} active={i === active} onHover={() => setActive(i)} onPick={() => commit(i)}>
                <span className="flex h-7 w-7 flex-none items-center justify-center rounded-md bg-[#A04A97]/[0.09] text-[#A04A97]">
                  <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 7a2 2 0 0 1 2-2h4l2 2.5h8a2 2 0 0 1 2 2V17a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  </svg>
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[14px] font-medium text-[#18101C]">{p.name}</span>
                  <span className="block truncate text-[12.5px] text-[#77707E]">
                    {p.status}
                    {p.document_count ? ` · ${p.document_count} document${p.document_count === 1 ? "" : "s"}` : " · no documents"}
                  </span>
                </span>
                {p.submitted && (
                  <span className="flex-none rounded-full bg-[#2DAD4E]/10 px-2 py-[2px] text-[11.5px] font-medium text-[#1F7D38]">
                    Submitted
                  </span>
                )}
              </Row>
            ))}

          {mode === "argument" && !loading && hasMore && (
            <Row
              data-idx={projects.length}
              active={active === projects.length}
              onHover={() => setActive(projects.length)}
              onPick={() => commit(projects.length)}
            >
              <span className="flex h-7 w-7 flex-none items-center justify-center rounded-md text-[#A04A97]">
                <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </span>
              <span className="text-[13.5px] font-medium text-[#A04A97]">
                {loadingMore ? "Loading…" : `Show more (${total - projects.length} left)`}
              </span>
            </Row>
          )}
        </div>

        <div className="border-t border-[#18101C]/[0.06] px-3 py-1.5 text-[11.5px] text-[#77707E]">
          ↑↓ to move · Enter to select · Esc to dismiss
        </div>
      </div>
    </div>
  );
};

export default CopilotSlashMenu;
